// Minimal, dependency-free QR Code encoder (Byte mode only) + canvas renderer, sized for
// encoding short form share links rather than arbitrary QR payloads.
// Implements the parts of ISO/IEC 18004 needed to do that: Reed-Solomon error correction,
// function-pattern placement, data placement and a single fixed mask pattern.
//
// Verified against a reference QR implementation (byte-mode segments, multiple versions/lengths,
// including the version-info block used from version 7 upward) and confirmed to decode
// correctly with a real QR scanner.

const BITS_PER_BYTE = 8
// Capped to the small end of the spec's 1-40 version range. Version 10 (~213 bytes) is plenty for a
// plain form share link and stays comfortably scannable; the URL Builder opts into version 20
// (~660 bytes) for pre-filled links, which carry parameters as well.
const DEFAULT_MAX_QR_VERSION = 10
const HIGHEST_SUPPORTED_QR_VERSION = 20
const MIN_VERSION_WITH_VERSION_INFO = 7
const FINDER_PATTERN_SIZE = 7
const TIMING_PATTERN_FIXED_COORDINATE = 6
const ALIGNMENT_PATTERN_RADIUS = 2
// Mask 0 is used unconditionally (see FIXED_MASK_PATTERN below) instead of scoring all 8
// candidate masks, so only this one pattern's bit needs computing.
const FIXED_MASK_PATTERN = 0

// ---- Galois Field GF(256) arithmetic, needed for Reed-Solomon error correction ----
const GF256_PRIMITIVE_POLYNOMIAL = 0x11D // x^8 + x^4 + x^3 + x^2 + 1, mandated by ISO/IEC 18004
const GF256_EXPONENT_TABLE = new Uint8Array(512)
const GF256_LOGARITHM_TABLE = new Uint8Array(256)

;(function buildGaloisFieldTables () {
  let value = 1
  for (let exponent = 0; exponent < 255; exponent++) {
    GF256_EXPONENT_TABLE[exponent] = value
    GF256_LOGARITHM_TABLE[value] = exponent
    value <<= 1
    if (value & 0x100) value ^= GF256_PRIMITIVE_POLYNOMIAL
  }
  for (let exponent = 255; exponent < 512; exponent++) {
    GF256_EXPONENT_TABLE[exponent] = GF256_EXPONENT_TABLE[exponent - 255]
  }
})()

function galoisMultiply (a, b) {
  if (a === 0 || b === 0) return 0
  return GF256_EXPONENT_TABLE[GF256_LOGARITHM_TABLE[a] + GF256_LOGARITHM_TABLE[b]]
}

function multiplyPolynomials (a, b) {
  const result = new Uint8Array(a.length + b.length - 1)
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      result[i + j] ^= galoisMultiply(a[i], b[j])
    }
  }
  return result
}

function generatorPolynomial (errorCorrectionCodewordCount) {
  let poly = new Uint8Array([1])
  for (let i = 0; i < errorCorrectionCodewordCount; i++) {
    poly = multiplyPolynomials(poly, new Uint8Array([1, GF256_EXPONENT_TABLE[i]]))
  }
  return poly
}

function polynomialRemainder (dividend, divisor) {
  let remainder = new Uint8Array(dividend)
  while (remainder.length - divisor.length >= 0) {
    const coefficient = remainder[0]
    for (let i = 0; i < divisor.length; i++) {
      remainder[i] ^= galoisMultiply(divisor[i], coefficient)
    }
    let leadingZeroCount = 0
    while (leadingZeroCount < remainder.length && remainder[leadingZeroCount] === 0) leadingZeroCount++
    remainder = remainder.slice(leadingZeroCount)
  }
  return remainder
}

function reedSolomonEncode (dataCodewords, errorCorrectionCodewordCount) {
  const generator = generatorPolynomial(errorCorrectionCodewordCount)
  const padded = new Uint8Array(dataCodewords.length + errorCorrectionCodewordCount)
  padded.set(dataCodewords)
  const remainder = polynomialRemainder(padded, generator)
  const errorCorrectionCodewords = new Uint8Array(errorCorrectionCodewordCount)
  errorCorrectionCodewords.set(remainder, errorCorrectionCodewordCount - remainder.length)
  return errorCorrectionCodewords
}

// ---- Per-version spec tables (ISO/IEC 18004 Annex), one entry per version 1-10 ----
// (the spec defines versions up to 40, but version 10 alone already holds 200+ bytes at the
// error correction level used here, far more than any form share link needs)
const TOTAL_CODEWORDS_BY_VERSION = [
  0, 26, 44, 70, 100, 134, 172, 196, 242, 292, 346,
  404, 466, 532, 581, 655, 733, 815, 901, 991, 1085
]

export const ERROR_CORRECTION_LEVEL = {
  LOW: { bit: 1, tableColumn: 0 },
  MEDIUM: { bit: 0, tableColumn: 1 },
  QUARTILE: { bit: 3, tableColumn: 2 },
  HIGH: { bit: 2, tableColumn: 3 }
}

// Columns per version ordered [LOW, MEDIUM, QUARTILE, HIGH], matching ERROR_CORRECTION_LEVEL.*.tableColumn
const ERROR_CORRECTION_CODEWORDS_BY_VERSION = [
  7, 10, 13, 17, 10, 16, 22, 28, 15, 26, 36, 44, 20, 36, 52, 64, 26, 48, 72, 88,
  36, 64, 96, 112, 40, 72, 108, 130, 48, 88, 132, 156, 60, 110, 160, 192, 72, 130, 192, 224,
  80, 150, 224, 264, 96, 176, 260, 308, 104, 198, 288, 352, 120, 216, 320, 384,
  132, 240, 360, 432, 144, 280, 408, 480, 168, 308, 448, 532, 180, 338, 504, 588,
  196, 364, 546, 650, 224, 416, 600, 700
]

const ERROR_CORRECTION_BLOCKS_BY_VERSION = [
  1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 1, 2, 2, 4, 1, 2, 4, 4,
  2, 4, 4, 4, 2, 4, 6, 5, 2, 4, 6, 6, 2, 5, 8, 8, 4, 5, 8, 8,
  4, 5, 8, 11, 4, 8, 10, 11, 4, 9, 12, 16, 4, 9, 16, 16,
  6, 10, 12, 18, 6, 10, 17, 16, 6, 11, 16, 19, 6, 13, 18, 21,
  7, 14, 21, 25, 8, 16, 20, 25
]

function getSymbolSize (version) {
  // Each higher version adds a 4-module ring around the previous one; version 1 is 21x21
  return version * 4 + 17
}

function getTotalCodewords (version) {
  return TOTAL_CODEWORDS_BY_VERSION[version]
}

function getErrorCorrectionCodewordCount (version, level) {
  return ERROR_CORRECTION_CODEWORDS_BY_VERSION[(version - 1) * 4 + level.tableColumn]
}

function getErrorCorrectionBlockCount (version, level) {
  return ERROR_CORRECTION_BLOCKS_BY_VERSION[(version - 1) * 4 + level.tableColumn]
}

// ---- Byte-mode encoding (the only mode this encoder supports; sufficient for any URL) ----
const BYTE_MODE_INDICATOR = 0b0100 // 4-bit mode indicator per ISO/IEC 18004 Table 2
const MODE_INDICATOR_BIT_LENGTH = 4
const TERMINATOR_BIT_LENGTH = 4
const PAD_CODEWORDS = [0xEC, 0x11]

function getCharacterCountIndicatorBitLength (version) {
  // Byte-mode character-count-indicator length per ISO/IEC 18004 Table 3
  return version < 10 ? 8 : 16
}

function getMaxByteCapacity (version, level) {
  const dataCodewordBits = (getTotalCodewords(version) - getErrorCorrectionCodewordCount(version, level)) * BITS_PER_BYTE
  const reservedBits = MODE_INDICATOR_BIT_LENGTH + getCharacterCountIndicatorBitLength(version)
  return Math.floor((dataCodewordBits - reservedBits) / BITS_PER_BYTE)
}

function selectVersionForData (byteLength, level, maxVersion = DEFAULT_MAX_QR_VERSION) {
  const highestVersion = Math.min(maxVersion, HIGHEST_SUPPORTED_QR_VERSION)
  for (let version = 1; version <= highestVersion; version++) {
    if (byteLength <= getMaxByteCapacity(version, level)) return version
  }
  throw new Error('Data is too long to fit in a QR code at the selected error correction level.')
}

function createBitBuffer () {
  return { bytes: [], bitLength: 0 }
}

function pushBit (buffer, bit) {
  const byteIndex = Math.floor(buffer.bitLength / BITS_PER_BYTE)
  if (buffer.bytes.length <= byteIndex) buffer.bytes.push(0)
  if (bit) buffer.bytes[byteIndex] |= (0x80 >>> (buffer.bitLength % BITS_PER_BYTE))
  buffer.bitLength++
}

function pushBits (buffer, value, bitCount) {
  for (let i = bitCount - 1; i >= 0; i--) pushBit(buffer, (value >>> i) & 1)
}

function interleaveWithErrorCorrection (dataCodewords, version, level) {
  const totalCodewords = getTotalCodewords(version)
  const blockCount = getErrorCorrectionBlockCount(version, level)
  const dataCodewordCount = dataCodewords.length

  const group2BlockCount = totalCodewords % blockCount
  const group1BlockCount = blockCount - group2BlockCount
  const group1DataCodewordCount = Math.floor(dataCodewordCount / blockCount)
  const group2DataCodewordCount = group1DataCodewordCount + 1
  const codewordsPerBlockTotal = Math.floor(totalCodewords / blockCount)
  const errorCorrectionCodewordsPerBlock = codewordsPerBlockTotal - group1DataCodewordCount

  const dataBlocks = []
  const errorCorrectionBlocks = []
  let offset = 0
  let maxDataBlockLength = 0

  for (let blockIndex = 0; blockIndex < blockCount; blockIndex++) {
    const blockLength = blockIndex < group1BlockCount ? group1DataCodewordCount : group2DataCodewordCount
    const block = dataCodewords.slice(offset, offset + blockLength)
    dataBlocks.push(block)
    errorCorrectionBlocks.push(reedSolomonEncode(block, errorCorrectionCodewordsPerBlock))
    offset += blockLength
    maxDataBlockLength = Math.max(maxDataBlockLength, blockLength)
  }

  const interleaved = []
  for (let i = 0; i < maxDataBlockLength; i++) {
    for (let blockIndex = 0; blockIndex < blockCount; blockIndex++) {
      if (i < dataBlocks[blockIndex].length) interleaved.push(dataBlocks[blockIndex][i])
    }
  }
  for (let i = 0; i < errorCorrectionCodewordsPerBlock; i++) {
    for (let blockIndex = 0; blockIndex < blockCount; blockIndex++) {
      interleaved.push(errorCorrectionBlocks[blockIndex][i])
    }
  }

  return interleaved
}

function buildDataCodewords (text, version, level) {
  const bytes = new TextEncoder().encode(text)
  const buffer = createBitBuffer()

  pushBits(buffer, BYTE_MODE_INDICATOR, MODE_INDICATOR_BIT_LENGTH)
  pushBits(buffer, bytes.length, getCharacterCountIndicatorBitLength(version))
  for (let i = 0; i < bytes.length; i++) pushBits(buffer, bytes[i], BITS_PER_BYTE)

  const dataCodewordCount = getTotalCodewords(version) - getErrorCorrectionCodewordCount(version, level)
  const dataCapacityBits = dataCodewordCount * BITS_PER_BYTE

  if (buffer.bitLength + TERMINATOR_BIT_LENGTH <= dataCapacityBits) {
    pushBits(buffer, 0, TERMINATOR_BIT_LENGTH)
  }
  while (buffer.bitLength % BITS_PER_BYTE !== 0) pushBit(buffer, 0)

  let padIndex = 0
  while (buffer.bytes.length < dataCodewordCount) {
    buffer.bytes.push(PAD_CODEWORDS[padIndex % PAD_CODEWORDS.length])
    padIndex++
  }

  return interleaveWithErrorCorrection(buffer.bytes, version, level)
}

// ---- Matrix (the grid of modules that makes up the visible QR code) ----
function createMatrix (size) {
  return { size, modules: new Uint8Array(size * size), reserved: new Uint8Array(size * size) }
}

function setModule (matrix, row, col, isDark, markReserved) {
  const index = row * matrix.size + col
  matrix.modules[index] = isDark ? 1 : 0
  if (markReserved) matrix.reserved[index] = 1
}

function getModule (matrix, row, col) {
  return matrix.modules[row * matrix.size + col]
}

function isReserved (matrix, row, col) {
  return matrix.reserved[row * matrix.size + col] === 1
}

function drawFinderPatterns (matrix) {
  const positions = [
    [0, 0],
    [matrix.size - FINDER_PATTERN_SIZE, 0],
    [0, matrix.size - FINDER_PATTERN_SIZE]
  ]

  for (const [row, col] of positions) {
    for (let r = -1; r <= FINDER_PATTERN_SIZE; r++) {
      if (row + r <= -1 || matrix.size <= row + r) continue
      for (let c = -1; c <= FINDER_PATTERN_SIZE; c++) {
        if (col + c <= -1 || matrix.size <= col + c) continue
        const isRing = (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
          (c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
          (r >= 2 && r <= 4 && c >= 2 && c <= 4)
        setModule(matrix, row + r, col + c, isRing, true)
      }
    }
  }
}

function drawTimingPatterns (matrix) {
  for (let i = 8; i < matrix.size - 8; i++) {
    const isDark = i % 2 === 0
    setModule(matrix, i, TIMING_PATTERN_FIXED_COORDINATE, isDark, true)
    setModule(matrix, TIMING_PATTERN_FIXED_COORDINATE, i, isDark, true)
  }
}

function getAlignmentPatternCoordinates (version, size) {
  if (version === 1) return []

  const patternCount = Math.floor(version / 7) + 2
  const lastCoordinate = size - FINDER_PATTERN_SIZE
  const interval = size === 145 ? 26 : Math.ceil((size - 13) / (2 * patternCount - 2)) * 2
  const coordinates = [lastCoordinate]

  for (let i = 1; i < patternCount - 1; i++) coordinates[i] = coordinates[i - 1] - interval
  coordinates.push(TIMING_PATTERN_FIXED_COORDINATE)

  return coordinates.reverse()
}

function drawAlignmentPatterns (matrix, version) {
  const coordinates = getAlignmentPatternCoordinates(version, matrix.size)

  for (let i = 0; i < coordinates.length; i++) {
    for (let j = 0; j < coordinates.length; j++) {
      const overlapsFinderPattern =
        (i === 0 && j === 0) ||
        (i === 0 && j === coordinates.length - 1) ||
        (i === coordinates.length - 1 && j === 0)
      if (overlapsFinderPattern) continue

      const row = coordinates[i]
      const col = coordinates[j]

      for (let r = -ALIGNMENT_PATTERN_RADIUS; r <= ALIGNMENT_PATTERN_RADIUS; r++) {
        for (let c = -ALIGNMENT_PATTERN_RADIUS; c <= ALIGNMENT_PATTERN_RADIUS; c++) {
          const isRing = r === -ALIGNMENT_PATTERN_RADIUS || r === ALIGNMENT_PATTERN_RADIUS ||
            c === -ALIGNMENT_PATTERN_RADIUS || c === ALIGNMENT_PATTERN_RADIUS ||
            (r === 0 && c === 0)
          setModule(matrix, row + r, col + c, isRing, true)
        }
      }
    }
  }
}

function getBchDigitCount (value) {
  let count = 0
  while (value !== 0) {
    count++
    value >>>= 1
  }
  return count
}

const VERSION_INFO_GENERATOR_POLYNOMIAL = 0x1F25 // (18,6) Golay code generator, ISO/IEC 18004 Annex D
const VERSION_INFO_BIT_LENGTH = 18

function encodeVersionInfo (version) {
  const generatorBitLength = getBchDigitCount(VERSION_INFO_GENERATOR_POLYNOMIAL)
  let remainder = version << 12
  while (getBchDigitCount(remainder) - generatorBitLength >= 0) {
    remainder ^= VERSION_INFO_GENERATOR_POLYNOMIAL << (getBchDigitCount(remainder) - generatorBitLength)
  }
  return (version << 12) | remainder
}

function drawVersionInfo (matrix, version) {
  const bits = encodeVersionInfo(version)
  for (let i = 0; i < VERSION_INFO_BIT_LENGTH; i++) {
    const row = Math.floor(i / 3)
    const col = (i % 3) + matrix.size - 8 - 3
    const isDark = ((bits >> i) & 1) === 1
    setModule(matrix, row, col, isDark, true)
    setModule(matrix, col, row, isDark, true)
  }
}

const FORMAT_INFO_GENERATOR_POLYNOMIAL = 0x537 // (15,5) BCH code generator, ISO/IEC 18004 Annex C
const FORMAT_INFO_XOR_MASK = 0x5412 // prevents an all-zero format string, per spec
const FORMAT_INFO_BIT_LENGTH = 15

function encodeFormatInfo (level, maskPattern) {
  const generatorBitLength = getBchDigitCount(FORMAT_INFO_GENERATOR_POLYNOMIAL)
  const data = (level.bit << 3) | maskPattern
  let remainder = data << 10
  while (getBchDigitCount(remainder) - generatorBitLength >= 0) {
    remainder ^= FORMAT_INFO_GENERATOR_POLYNOMIAL << (getBchDigitCount(remainder) - generatorBitLength)
  }
  return ((data << 10) | remainder) ^ FORMAT_INFO_XOR_MASK
}

function drawFormatInfo (matrix, level, maskPattern) {
  const bits = encodeFormatInfo(level, maskPattern)
  const size = matrix.size

  for (let i = 0; i < FORMAT_INFO_BIT_LENGTH; i++) {
    const isDark = ((bits >> i) & 1) === 1

    if (i < 6) setModule(matrix, i, 8, isDark, true)
    else if (i < 8) setModule(matrix, i + 1, 8, isDark, true)
    else setModule(matrix, size - 15 + i, 8, isDark, true)

    if (i < 8) setModule(matrix, 8, size - i - 1, isDark, true)
    else if (i < 9) setModule(matrix, 8, 15 - i, isDark, true)
    else setModule(matrix, 8, 14 - i, isDark, true)
  }

  setModule(matrix, size - 8, 8, true, true) // fixed dark module, always present
}

function drawDataCodewords (matrix, codewords) {
  const size = matrix.size
  let direction = -1
  let row = size - 1
  let bitIndex = 7
  let byteIndex = 0

  for (let col = size - 1; col > 0; col -= 2) {
    if (col === TIMING_PATTERN_FIXED_COORDINATE) col--

    while (true) {
      for (let c = 0; c < 2; c++) {
        const currentCol = col - c
        if (!isReserved(matrix, row, currentCol)) {
          const isDark = byteIndex < codewords.length && ((codewords[byteIndex] >>> bitIndex) & 1) === 1
          setModule(matrix, row, currentCol, isDark, false)
          bitIndex--
          if (bitIndex === -1) {
            byteIndex++
            bitIndex = 7
          }
        }
      }

      row += direction
      if (row < 0 || row >= size) {
        row -= direction
        direction = -direction
        break
      }
    }
  }
}

function getMaskBit (maskPattern, row, col) {
  switch (maskPattern) {
    case 0: return (row + col) % 2 === 0
    case 1: return row % 2 === 0
    case 2: return col % 3 === 0
    case 3: return (row + col) % 3 === 0
    case 4: return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0
    case 5: return (row * col) % 2 + (row * col) % 3 === 0
    case 6: return ((row * col) % 2 + (row * col) % 3) % 2 === 0
    case 7: return ((row * col) % 3 + (row + col) % 2) % 2 === 0
    default: throw new Error(`Invalid mask pattern: ${maskPattern}`)
  }
}

function applyMask (matrix, maskPattern) {
  for (let row = 0; row < matrix.size; row++) {
    for (let col = 0; col < matrix.size; col++) {
      if (isReserved(matrix, row, col)) continue
      const index = row * matrix.size + col
      matrix.modules[index] ^= getMaskBit(maskPattern, row, col) ? 1 : 0
    }
  }
}

/**
 * Encodes text into a QR Code module matrix (Byte mode).
 * @returns {{ size: number, isDark: (row: number, col: number) => boolean }}
 */
export function generateQrMatrix (
  text,
  errorCorrectionLevel = ERROR_CORRECTION_LEVEL.MEDIUM,
  maxVersion = DEFAULT_MAX_QR_VERSION
) {
  const byteLength = new TextEncoder().encode(text).length
  const version = selectVersionForData(byteLength, errorCorrectionLevel, maxVersion)
  const codewords = buildDataCodewords(text, version, errorCorrectionLevel)
  const matrix = createMatrix(getSymbolSize(version))

  drawFinderPatterns(matrix)
  drawTimingPatterns(matrix)
  drawAlignmentPatterns(matrix, version)
  drawFormatInfo(matrix, errorCorrectionLevel, FIXED_MASK_PATTERN) // reserve format-info bits before placing data
  if (version >= MIN_VERSION_WITH_VERSION_INFO) drawVersionInfo(matrix, version)
  drawDataCodewords(matrix, codewords)
  applyMask(matrix, FIXED_MASK_PATTERN)

  return { size: matrix.size, isDark: (row, col) => getModule(matrix, row, col) === 1 }
}

// ---- Canvas rendering ----
const DEFAULT_CANVAS_SIZE_PX = 240
const DEFAULT_QUIET_ZONE_MODULES = 4 // minimum quiet zone recommended by ISO/IEC 18004
const QR_DARK_MODULE_COLOR = '#000000'
const QR_LIGHT_MODULE_COLOR = '#ffffff'
// A denser code needs more pixels per module to stay readable on screen and in print. Version 20 is
// 97 modules wide, which at the 240px default would leave ~2px per module.
const MIN_MODULE_PIXEL_SIZE = 4

/**
 * Renders text as a QR code onto the given canvas element.
 */
export function drawQrCode (canvas, text, {
  sizePx = DEFAULT_CANVAS_SIZE_PX,
  quietZoneModules = DEFAULT_QUIET_ZONE_MODULES,
  errorCorrectionLevel = ERROR_CORRECTION_LEVEL.MEDIUM,
  maxVersion = DEFAULT_MAX_QR_VERSION
} = {}) {
  const matrix = generateQrMatrix(text, errorCorrectionLevel, maxVersion)
  const modulesAcross = matrix.size + quietZoneModules * 2
  const modulePixelSize = Math.max(MIN_MODULE_PIXEL_SIZE, Math.floor(sizePx / modulesAcross))
  const canvasSizePx = modulePixelSize * (matrix.size + quietZoneModules * 2)

  canvas.width = canvasSizePx
  canvas.height = canvasSizePx

  const context = canvas.getContext('2d')
  context.fillStyle = QR_LIGHT_MODULE_COLOR
  context.fillRect(0, 0, canvasSizePx, canvasSizePx)
  context.fillStyle = QR_DARK_MODULE_COLOR

  for (let row = 0; row < matrix.size; row++) {
    for (let col = 0; col < matrix.size; col++) {
      if (!matrix.isDark(row, col)) continue
      const x = (col + quietZoneModules) * modulePixelSize
      const y = (row + quietZoneModules) * modulePixelSize
      context.fillRect(x, y, modulePixelSize, modulePixelSize)
    }
  }
}
